<template>
    <div>
        <div class="d-flex align-center mb-1">

            <v-btn
                color="secondary"
                text
                small
                rounded
                @click="showCreateDialog"
            >
                <v-icon class="mr-2">mdi-plus-circle-outline</v-icon>
                {{ $t('views.main.complaints.complaints.new_complaint_button') }}
            </v-btn>

            <v-spacer></v-spacer>

            <span class="body-2 text--secondary d-none d-sm-inline">
                {{ from }}-{{ to }} {{ $t('views.main.complaints.complaints.pagination_of') }} {{ total }}
            </span>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :disabled="isPageFirst" @click="prevPage">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('views.main.complaints.complaints.pagination_prev_page') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :disabled="isPageLast" @click="nextPage">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('views.main.complaints.complaints.pagination_next_page') }}</span>
            </v-tooltip>
        </div>

        <v-divider></v-divider>

        <loading-message
            v-if="reclamationIndexing"
            :message="$t('views.main.complaints.complaints.loading_complaints')"
            class="my-5"></loading-message>

        <v-simple-table v-else-if="reclamations.length">
            <template v-slot:default>
                <tbody>
                <tr
                    v-for="reclamation in reclamations"
                    :key="reclamation.ReclamationID"
                >
                    <th class="text-no-wrap">
                        {{ reclamation.ReclamationName }}
                    </th>
                    <td><v-chip x-small color="info" class="my-1">{{ reclamation.ReasonName }}</v-chip></td>
                    <td>{{ reclamation.Comments }}</td>
                    <td :class="reclamation.Status === 1 ? 'warning--text text-no-wrap' : 'success--text text-no-wrap'">
                        {{ reclamation.StatusName }}
                    </td>
                    <td class="text-no-wrap">{{ reclamation.Date | dateConvenientFormat }}</td>
                    <td class="text-right">

                        <v-menu
                            bottom
                            left
                            :offset-y="true"
                            rounded
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    small
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>

                            <v-list dense>
                                <v-list-item>
                                    <v-list-item-title>
                                        <a @click="showEditDialog(reclamation)">
                                            <v-icon small>mdi-folder-edit-outline</v-icon>
                                            {{ $t('views.main.complaints.complaints.edit') }}
                                        </a>
                                    </v-list-item-title>
                                </v-list-item>

                                <v-list-item>
                                    <v-list-item-title>
                                        <a @click="showDeleteDialog(reclamation)">
                                            <v-icon small>mdi-delete-outline</v-icon>
                                            {{ $t('views.main.complaints.complaints.delete') }}
                                        </a>
                                    </v-list-item-title>
                                </v-list-item>

                            </v-list>
                        </v-menu>

                    </td>
                </tr>
                </tbody>
            </template>
        </v-simple-table>

        <v-alert
            v-else
            class="mt-5"
            border="left"
            colored-border
            type="info"
            elevation="1"
        >{{ $t('views.main.complaints.complaints.no_complaints') }}</v-alert>

        <v-dialog
            v-model="reclamationCreateDialog"
            persistent
            width="500"
        >
            <v-card>
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.complaints.complaints.new_complaint') }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="hideCreateDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <v-select
                        v-model="reclamationForm.Reason"
                        :items="reclamationReasons"
                        item-text="ReasonName"
                        item-value="ReasonID"
                        :label="$t('views.main.complaints.complaints.reason_name_label')+'*'"
                        :loading="reclamationReasonIndexing"
                        :error="$store.getters.hasValidationError('Reason')"
                        :error-messages="$store.getters.validationError('Reason')"
                    ></v-select>
                    <v-select
                        v-model="reclamationForm.SystemID"
                        :items="systems"
                        item-text="SystemName"
                        item-value="SystemID"
                        :label="$t('views.main.complaints.complaints.system_name_label')+'*'"
                        :loading="systemIndexing"
                        :disabled="!systems.length || reclamationForm.Reason === null"
                        :error="$store.getters.hasValidationError('SystemID')"
                        :error-messages="$store.getters.validationError('SystemID')"
                    ></v-select>
                    <v-select
                        v-model="reclamationForm.BlockID"
                        :items="blocks"
                        item-text="BlockName"
                        item-value="BlockID"
                        :label="$t('views.main.complaints.complaints.block_name_label')+'*'"
                        :loading="blockIndexing"
                        :disabled="!blocks.length"
                        :error="$store.getters.hasValidationError('BlockID')"
                        :error-messages="$store.getters.validationError('BlockID')"
                    ></v-select>
                    <v-select
                        v-model="reclamationForm.ComponentID"
                        :items="components"
                        item-text="ComponentName"
                        item-value="ComponentID"
                        :label="$t('views.main.complaints.complaints.component_name_label')+'*'"
                        :loading="componentIndexing"
                        :disabled="!components.length"
                        :error="$store.getters.hasValidationError('ComponentID')"
                        :error-messages="$store.getters.validationError('ComponentID')"
                    ></v-select>
                    <v-select
                        v-model="reclamationForm.UnitColorID"
                        :items="colors"
                        item-text="UnitColorName"
                        item-value="UnitColorID"
                        :label="$t('views.main.complaints.complaints.unit_color_name_label')+'*'"
                        :loading="colorIndexing"
                        :disabled="!colors.length"
                        :error="$store.getters.hasValidationError('UnitColorID')"
                        :error-messages="$store.getters.validationError('UnitColorID')"
                    ></v-select>
                    <v-select
                        v-model="reclamationForm.LabelsNo"
                        :items="quantities"
                        :label="$t('views.main.complaints.complaints.labels_no_label')+'*'"
                        :disabled="reclamationForm.UnitColorID === null"
                        :error="$store.getters.hasValidationError('LabelsNo')"
                        :error-messages="$store.getters.validationError('LabelsNo')"
                    ></v-select>
                    <v-textarea
                        v-model="reclamationForm.WebAppComments"
                        :label="$t('views.main.complaints.complaints.web_app_comments_label')+'*'"
                        maxlength="185"
                        :counter="185"
                        :disabled="reclamationForm.UnitColorID === null"
                        :error="$store.getters.hasValidationError('WebAppComments')"
                        :error-messages="$store.getters.validationError('WebAppComments')"
                    ></v-textarea>
                    <v-text-field
                        v-model="reclamationForm.ClientReclamationName"
                        :label="$t('views.main.complaints.complaints.client_reclamation_name_label')"
                        :hint="$t('views.main.complaints.complaints.client_reclamation_name_hint')"
                        maxlength="30"
                        :counter="30"
                        :disabled="reclamationForm.UnitColorID === null"
                        :error="$store.getters.hasValidationError('ClientReclamationName')"
                        :error-messages="$store.getters.validationError('ClientReclamationName')"
                    ></v-text-field>

                    <small>* {{ $t('views.main.complaints.complaints.field_required') }}</small>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        rounded
                        color="success"
                        @click="reclamationStore"
                        :loading="reclamationStoring"
                    >{{ $t('views.main.complaints.complaints.send_button') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="reclamationEditDialog"
            persistent
            width="500"
        >
            <v-card v-if="reclamationToEdit">
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.complaints.complaints.complaint') }} {{reclamationToEdit.ReclamationName}}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="hideEditDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text v-if="!reclamationToEdit.IsFromWebApp">
                    {{ $t('views.main.complaints.complaints.cant_edit_manufacturer_complaints') }}
                </v-card-text>
                <v-card-text v-else-if="!reclamationToEdit.IsEditable">
                    {{ $t('views.main.complaints.complaints.cant_edit_complaints_with_status') }}
                    <strong>{{ reclamationToEdit.StatusName }}</strong>.
                </v-card-text>
                <v-card-text v-else>
                    <p class="mb-1">
                        {{ $t('views.main.complaints.complaints.system_name_label') }}:
                        <strong>{{reclamationToEdit.System.SystemName}}</strong>
                    </p>
                    <p class="mb-1">
                        {{ $t('views.main.complaints.complaints.block_name_label') }}:
                        <strong>{{reclamationToEdit.Block.BlockName}}</strong>
                    </p>
                    <p class="mb-1">
                        {{ $t('views.main.complaints.complaints.component_name_label') }}:
                        <strong>{{reclamationToEdit.Component.ComponentName}}</strong>
                    </p>
                    <p>
                        {{ $t('views.main.complaints.complaints.unit_color_name_label') }}:
                        <strong>{{reclamationToEdit.UnitColor.UnitColorName}}</strong>
                    </p>

                    <v-select
                        v-model="reclamationForm.Reason"
                        :items="reclamationReasons"
                        item-text="ReasonName"
                        item-value="ReasonID"
                        :label="$t('views.main.complaints.complaints.reason_name_label')+'*'"
                        :loading="reclamationReasonIndexing"
                        :error="$store.getters.hasValidationError('Reason')"
                        :error-messages="$store.getters.validationError('Reason')"
                    ></v-select>
                    <v-select
                        v-model="reclamationForm.LabelsNo"
                        :items="quantities"
                        :label="$t('views.main.complaints.complaints.labels_no_label')+'*'"
                        :error="$store.getters.hasValidationError('LabelsNo')"
                        :error-messages="$store.getters.validationError('LabelsNo')"
                    ></v-select>
                    <v-textarea
                        v-model="reclamationForm.WebAppComments"
                        :label="$t('views.main.complaints.complaints.web_app_comments_label')+'*'"
                        maxlength="185"
                        :counter="185"
                        :error="$store.getters.hasValidationError('WebAppComments')"
                        :error-messages="$store.getters.validationError('WebAppComments')"
                    ></v-textarea>
                    <v-text-field
                        v-model="reclamationForm.ClientReclamationName"
                        :label="$t('views.main.complaints.complaints.client_reclamation_name_label')"
                        :hint="$t('views.main.complaints.complaints.client_reclamation_name_hint')"
                        maxlength="30"
                        :counter="30"
                        :error="$store.getters.hasValidationError('ClientReclamationName')"
                        :error-messages="$store.getters.validationError('ClientReclamationName')"
                    ></v-text-field>

                    <small>* {{ $t('views.main.complaints.complaints.field_required') }}</small>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="reclamationToEdit.IsEditable"
                        rounded
                        color="success"
                        @click="reclamationUpdate"
                        :loading="reclamationUpdating"
                    >{{ $t('views.main.complaints.complaints.save_button') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="reclamationDeleteDialog"
            persistent
            width="500"
        >
            <v-card v-if="reclamationToDelete">
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.complaints.complaints.complaint_removal') }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="hideDeleteDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text v-if="!reclamationToDelete.IsFromWebApp">
                    {{ $t('views.main.complaints.complaints.cant_delete_manufacturer_complaints') }}
                </v-card-text>
                <v-card-text v-else-if="!reclamationToDelete.IsEditable">
                    {{ $t('views.main.complaints.complaints.cant_delete_complaints_with_status') }}
                    <strong>{{ reclamationToDelete.StatusName }}</strong>.
                </v-card-text>
                <v-card-text v-else>
                    {{ $t('views.main.complaints.complaints.sure_to_remove_complaint_with_number') }}
                    <strong>{{reclamationToDelete.ReclamationName}}</strong>?
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="reclamationToDelete.IsEditable"
                        color="error"
                        rounded
                        @click="reclamationDelete(reclamationToDelete)"
                        :loading="reclamationDeleting"
                    >{{ $t('views.main.complaints.complaints.delete_button') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
    import LoadingMessage from "../../../components/LoadingMessage";

    export default {
        name: "Complaints",

        components: {
            LoadingMessage,
        },

        data() {
            return {
                reclamationIndexing: false,
                reclamations: [],

                // pagination
                page: 1,
                lastPage:1,
                from: 0,
                to: 0,
                total: 0,

                reclamationCreateDialog: false,
                reclamationStoring: false,

                reclamationEditDialog: null,
                reclamationToEdit: null,
                reclamationUpdating: false,

                reclamationDeleteDialog: false,
                reclamationToDelete: null,
                reclamationDeleting: false,

                reclamationReasonIndexing: false,
                reclamationReasons: [],

                reclamationForm: {
                    Reason: null,
                    SystemID: null,
                    BlockID: null,
                    ComponentID: null,
                    UnitColorID: null,
                    LabelsNo: null,
                    WebAppComments: null,
                    ClientReclamationName: null,
                },

                systemIndexing: false,
                systems: [],

                blockIndexing: false,
                blocks: [],

                componentIndexing: false,
                components: [],

                colorIndexing: false,
                colors: [],

                quantities: [1,2,3,4,5,6,7,8,9,10],
            }
        },

        computed: {
            is_mobile() {
                return this.$vuetify.breakpoint.mobile;
            },
            isPageFirst() {
                return this.page === 1;
            },
            isPageLast() {
                return this.page === this.lastPage;
            },
            SystemID () {
                return this.reclamationForm.SystemID;
            },
            BlockID () {
                return this.reclamationForm.BlockID;
            },
            ComponentID () {
                return this.reclamationForm.ComponentID;
            },
            UnitColorID () {
                return this.reclamationForm.UnitColorID;
            },
            locale: function () {
                return this.$i18n.locale;
            },
        },

        methods: {
            reclamationIndex: function () {
                this.reclamationIndexing = true;

                this.$store.dispatch('reclamationIndex', {
                    page: this.page
                }).then(response => {
                    if (response.status === 200) {
                        this.reclamations = response.data.data;
                        this.page = response.data.meta.current_page ?? 1;
                        this.lastPage = response.data.meta.last_page ?? 1;
                        this.from = response.data.meta.from ?? 0;
                        this.to = response.data.meta.to ?? 0;
                        this.total = response.data.meta.total;
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.complaints.complaints.loading_complaints_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.reclamationIndexing = false;
                    });
            },
            reclamationStore: function () {
                this.reclamationStoring = true;

                this.$store.dispatch('reclamationStore', this.reclamationForm).then(response => {
                    if ([200, 201].includes(response.status)) {
                        this.hideCreateDialog();
                        this.$store.commit(
                            "showSnackbarSuccess",
                            this.$t('views.main.complaints.complaints.complaint_created')
                        );
                        this.reclamationIndex();
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.complaints.complaints.complaint_creation_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.reclamationStoring = false;
                    });
            },
            reclamationUpdate: function () {
                this.reclamationUpdating = true;

                this.$store.dispatch('reclamationUpdate', {
                    ReclamationID: this.reclamationToEdit.ReclamationID,
                    formData: this.reclamationForm
                }).then(response => {
                    if ([200, 201].includes(response.status)) {
                        this.hideEditDialog();
                        this.$store.commit(
                            "showSnackbarSuccess",
                            this.$t('views.main.complaints.complaints.complaint_updated')
                        );
                        this.reclamationIndex();
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.complaints.complaints.complaint_update_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.reclamationUpdating = false;
                    });
            },
            reclamationDelete: function (reclamation) {
                this.reclamationDeleting = true;

                this.$store.dispatch('reclamationDelete', reclamation.ReclamationID).then(response => {
                    if (response.status === 204) {
                        this.hideDeleteDialog();
                        this.$store.commit(
                            "showSnackbarSuccess",
                            this.$t('views.main.complaints.complaints.complaint_deleted')
                        );
                        this.reclamationIndex();
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.complaints.complaints.complaint_deleting_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.reclamationDeleting = false;
                    });
            },
            reclamationReasonIndex: function () {
                this.reclamationReasonIndexing = true;

                this.$store.dispatch('reclamationReasonIndex').then(response => {
                    if (response.status === 200) {
                        this.reclamationReasons = response.data.data;
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.complaints.complaints.loading_reasons_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.reclamationReasonIndexing = false;
                    });
            },
            systemIndex: function () {
                this.systemIndexing = true;

                this.$store.dispatch('loadSystems').then(response => {
                    if (response.status !== 200) {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.complaints.complaints.loading_systems_failed')
                        );
                        return;
                    }

                    this.systems = response.data.data;
                })
                    .catch(() => {})
                    .then(() => {
                        this.systemIndexing = false;
                    });
            },
            systemBlockIndex: function (SystemID) {
                this.blockIndexing = true;

                this.$store.dispatch('loadSystemBlocks', SystemID).then(response => {
                    if (response.status !== 200) {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.complaints.complaints.loading_blocks_failed')
                        );
                        return;
                    }

                    this.blocks = response.data.data;
                })
                    .catch(() => {})
                    .then(() => {
                        this.blockIndexing = false;
                    });
            },
            blockComponentIndex: function (BlockID) {
                this.componentIndexing = true;

                this.$store.dispatch('blockComponentIndex', BlockID).then(response => {
                    if (response.status !== 200) {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.complaints.complaints.loading_components_failed')
                        );
                        return;
                    }

                    this.components = response.data.data;
                })
                    .catch(() => {})
                    .then(() => {
                        this.componentIndexing = false;
                    });
            },
            blockComponentColorIndex: function (BlockID, ComponentID) {
                this.colorIndexing = true;

                this.$store.dispatch('blockComponentColorIndex', {
                    BlockID,
                    ComponentID
                }).then(response => {
                    if (response.status !== 200) {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.complaints.complaints.loading_colors_failed')
                        );
                        return;
                    }

                    this.colors = response.data.data;
                })
                    .catch(() => {})
                    .then(() => {
                        this.colorIndexing = false;
                    });
            },
            nextPage: function () {
                if(this.page < this.lastPage) this.page++;
            },
            prevPage: function () {
                if(this.page > 1) this.page--;
            },
            showCreateDialog: function () {
                this.reclamationCreateDialog = true;
            },
            hideCreateDialog: function () {
                this.reclamationCreateDialog = false;
                this.resetReclamationForm();
                this.$store.commit("resetValidation");
            },
            showEditDialog: function (reclamation) {
                this.reclamationToEdit = reclamation;
            },
            hideEditDialog: function () {
                this.reclamationEditDialog = false;
            },
            showDeleteDialog: function (reclamation) {
                this.reclamationToDelete = reclamation;
            },
            hideDeleteDialog: function () {
                this.reclamationDeleteDialog = false;
            },
            resetReclamationForm: function () {
                for (const index in this.reclamationForm) {
                    this.reclamationForm[index] = null;
                }
            },
            setReclamationForm: function (reclamation) {
                this.reclamationForm.Reason = reclamation.Reason;
                this.reclamationForm.LabelsNo = reclamation.LabelsNo;
                this.reclamationForm.WebAppComments = reclamation.WebAppComments;
                this.reclamationForm.ClientReclamationName = reclamation.ClientReclamationName;
            },
        },

        created() {
            this.debouncedReclamationIndex = _.debounce(this.reclamationIndex, 300);

            this.reclamationIndex();
            this.reclamationReasonIndex();
            this.systemIndex();
        },

        watch: {
            page: function () {
                if(this.$store.state.sources.reclamation_index) {
                    this.$store.state.sources.reclamation_index.cancel();
                }

                this.debouncedReclamationIndex();
            },
            reclamationToEdit: function (reclamation) {
                if(reclamation !== null) {
                    this.setReclamationForm(reclamation);
                    this.reclamationEditDialog = true;
                }
            },
            reclamationEditDialog: function (visible) {
                if(!visible) {
                    this.resetReclamationForm();
                    this.$store.commit("resetValidation");
                    this.reclamationToEdit = null;
                }
            },
            reclamationToDelete: function (reclamation) {
                if(reclamation !== null) {
                    this.reclamationDeleteDialog = true;
                }
            },
            reclamationDeleteDialog: function (visible) {
                if(!visible) {
                    this.reclamationToDelete = null;
                }
            },
            SystemID: function (newSystemID) {
                this.reclamationForm.BlockID = null;
                this.blocks = [];

                if(newSystemID === null) return;

                this.systemBlockIndex(newSystemID);
            },
            BlockID: function (newBlockID) {
                this.reclamationForm.ComponentID = null;
                this.components = [];

                if(newBlockID === null) return;

                this.blockComponentIndex(newBlockID);
            },
            ComponentID: function (newComponentID) {
                this.reclamationForm.UnitColorID = null;
                this.colors = [];

                if(newComponentID === null) return;

                this.blockComponentColorIndex(this.BlockID, newComponentID);
            },
            locale() {
                this.reclamationIndex();
            },
        },
    }
</script>

<style scoped>

</style>
